import { Cancel01Icon } from '@hugeicons/core-free-icons';
import {
  useBulkGetAdminProfilesAndEmails,
  useGetSession,
  useGetSessionAttendees,
  useGetSessionLocation,
  useGetUserViewedSessions,
  useMarkSessionsAsViewed,
} from '@sunrise-ui/api-client';
import { SessionStatus } from '@sunrise-ui/api/events/api';
import type { WithResponsive } from '@sunrise-ui/primitives';
import {
  COMMUNITY_APP_URL,
  Colors,
  ExpandableText,
  Header,
  HeaderButton,
  Loader,
  LogoIcon,
  Modal,
  TextBold,
  TextReg,
  formatLongDate,
  formatLongDateWithYear,
  formatTimeRange,
  useAuthStore,
  useDynamicStyles,
  useMediaQueries,
  useSafeLocalSearchParams,
} from '@sunrise-ui/primitives';
import { router, useFocusEffect } from 'expo-router';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import {
  Linking,
  Platform,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
} from 'react-native';

import { ProfileBio } from '../profiles/ProfileBio';
import { ProfileResult } from '../profiles/ProfileResult';
import { DesktopModalHeader } from './DesktopModalHeader';
import { GettingThereModal } from './GettingThereModal';
import { SessionCardTags } from './SessionCardTags';
import { SessionEditButton } from './SessionEditButton';
import { SessionFavoriteButton } from './SessionFavoriteButton';
import { SessionShareButton } from './SessionShareButton';
import { SessionStatusMenuButton } from './SessionStatusMenuButton';
import { SessionTicketCTA } from './SessionTicketCTA';
import { SessionTimeLocationActions } from './SessionTimeLocationActions';
import { SessionVoteButton } from './SessionVoteButton';

type Props = {
  isManagerView?: boolean;
  communityId?: string;
};

export function SessionDetailsModal({
  isManagerView = false,
  communityId,
}: Props) {
  const { eventId, sessionId } = useSafeLocalSearchParams<{
    eventId?: string;
    sessionId?: string;
  }>();
  const styles = useDynamicStyles(createStyles);
  const { isMobile } = useMediaQueries();
  const { isAuthenticated, currentUserId } = useAuthStore();
  const [isModalVisible, setIsModalVisible] = useState(!!sessionId);
  const [isGettingThereModalVisible, setIsGettingThereModalVisible] =
    useState(false);
  const { mutate: markSessionsAsViewed } = useMarkSessionsAsViewed();

  const { data: session, isLoading: isLoadingSession } =
    useGetSession(sessionId);
  const { data: speakers } = useGetSessionAttendees(sessionId, true);

  // Get session location
  const sessionLocationId = session?.location_id;
  const { data: sessionLocation, isLoading: isLoadingSessionLocation } =
    useGetSessionLocation(sessionLocationId);
  const { name: sessionLocationName } = sessionLocation ?? {};

  // Manager-only: fetch speaker/moderator emails for scheduled sessions
  const speakerUserIds = useMemo(() => {
    return (
      speakers
        ?.map(({ user_id }) => user_id)
        .filter((id): id is string => Boolean(id)) || []
    );
  }, [speakers]);
  const shouldFetchEmails =
    isManagerView &&
    session?.status === SessionStatus.Scheduled &&
    speakerUserIds.length > 0;
  const { data: { emailMap } = {} } = useBulkGetAdminProfilesAndEmails(
    shouldFetchEmails ? speakerUserIds : [],
  );
  const speakerModeratorEmails = useMemo(() => {
    if (!shouldFetchEmails) return [] as string[];
    return speakerUserIds
      .map((id) => emailMap?.get(id))
      .filter((email): email is string => Boolean(email));
  }, [shouldFetchEmails, speakerUserIds, emailMap]);

  // Fetch viewed sessions to check if this session has already been viewed
  const { data: viewedSessions } = useGetUserViewedSessions(
    eventId,
    currentUserId,
  );

  // Safe access with proper typing
  // Allow editing if user is creator or a speaker, or in manager view
  const canEditSession = !!(
    isManagerView ||
    session?.user_id === currentUserId ||
    (speakers && speakers.some((speaker) => speaker.user_id === currentUserId))
  );

  const hideSessionModal = () => {
    setIsModalVisible(false);
    try {
      router.setParams({ sessionId: undefined });
    } catch (error) {
      // Ignore navigation errors during unmount or before mount
      console.debug('Unable to clear sessionId param:', error);
    }
  };

  // Mark session as viewed when modal is opened (only if not already viewed)
  useEffect(() => {
    if (sessionId && isModalVisible && isAuthenticated && viewedSessions) {
      // Check if session has already been viewed
      const isAlreadyViewed = viewedSessions.some(
        (viewedSession: { id: string }) => viewedSession.id === sessionId,
      );

      // Only mark as viewed if it hasn't been viewed yet
      if (!isAlreadyViewed) {
        markSessionsAsViewed([sessionId]);
      }
    }
  }, [
    sessionId,
    isModalVisible,
    isAuthenticated,
    viewedSessions,
    markSessionsAsViewed,
  ]);

  const header = isMobile ? (
    <Header
      headerRight={
        <HeaderButton icon={Cancel01Icon} onPress={hideSessionModal} />
      }
      mobileLogo={LogoIcon}
    />
  ) : (
    <DesktopModalHeader
      title={session?.topic_name}
      onClose={hideSessionModal}
    />
  );

  useLayoutEffect(() => {
    if (sessionId && !isModalVisible) setIsModalVisible(true);

    if (!sessionId && isModalVisible) setIsModalVisible(false);
  }, [sessionId]);

  useFocusEffect(
    useCallback(() => {
      return () => {
        hideSessionModal();
      };
    }, []),
  );

  // Build session time and date display
  const timeRange = formatTimeRange(session?.start_date, session?.end_date);
  const formattedSessionDate = isManagerView
    ? formatLongDateWithYear(session?.start_date)
    : session?.status === SessionStatus.Scheduled
      ? formatLongDate(session?.start_date)
      : null;
  const formattedSessionTime = timeRange
    ? formattedSessionDate && !isManagerView
      ? `${formattedSessionDate}, ${timeRange}`
      : timeRange
    : null;

  return (
    <Modal
      visible={isModalVisible}
      hide={hideSessionModal}
      header={header}
      contentStyle={styles.modalContent}
    >
      <View style={styles.container}>
        {isLoadingSession ? (
          <Loader style={styles.loader} />
        ) : (
          <ScrollView contentContainerStyle={styles.scrollContent}>
            <View style={styles.sessionInfo}>
              {isMobile && (
                <TextBold style={styles.title} numberOfLines={2}>
                  {session?.topic_name}
                </TextBold>
              )}
              {isManagerView ? (
                <SessionTimeLocationActions
                  formattedDate={formattedSessionDate}
                  formattedTime={timeRange}
                  locationName={sessionLocationName}
                  isLoadingLocation={isLoadingSessionLocation}
                  actions={
                    <>
                      <SessionEditButton
                        canEdit={canEditSession}
                        sessionId={sessionId ?? ''}
                        eventId={eventId ?? ''}
                        communityId={communityId}
                      />
                      <SessionStatusMenuButton
                        sessionId={sessionId ?? ''}
                        eventId={eventId ?? ''}
                        currentStatus={session?.status}
                        speakerUserIds={speakerUserIds}
                        communityId={communityId}
                        existingStartDate={session?.start_date}
                        existingEndDate={session?.end_date}
                      />
                    </>
                  }
                />
              ) : (
                formattedSessionTime && (
                  <View style={styles.timeContainer}>
                    <TextReg style={styles.sessionTime}>
                      {formattedSessionTime}
                    </TextReg>
                  </View>
                )
              )}
              <View style={styles.tagRow}>
                <View style={styles.tagsContainer}>
                  <SessionCardTags
                    sessionId={sessionId ?? ''}
                    eventId={eventId ?? ''}
                  />
                </View>
                {!isManagerView && (
                  <View style={styles.actionButtons}>
                    {session?.status === SessionStatus.Voting && (
                      <SessionVoteButton
                        sessionId={sessionId}
                        eventId={eventId ?? ''}
                        communityId={communityId}
                        isLoading={isLoadingSession}
                      />
                    )}
                    {(session?.status === SessionStatus.Scheduled ||
                      session?.status === SessionStatus.Confirmed) && (
                      <>
                        <SessionFavoriteButton
                          sessionId={sessionId ?? ''}
                          eventId={eventId ?? ''}
                        />
                        <SessionTicketCTA
                          eventId={eventId ?? ''}
                          sessionId={sessionId ?? ''}
                        />
                      </>
                    )}
                    {eventId &&
                      (session?.status === SessionStatus.Accepted ||
                        session?.status === SessionStatus.Voting ||
                        session?.status === SessionStatus.Scheduled ||
                        session?.status === SessionStatus.Confirmed) && (
                        <>
                          {session?.status !== SessionStatus.Scheduled &&
                            session?.status !== SessionStatus.Confirmed && (
                              <SessionEditButton
                                canEdit={canEditSession}
                                sessionId={sessionId ?? ''}
                                eventId={eventId ?? ''}
                                communityId={communityId}
                              />
                            )}
                          {(session?.status === SessionStatus.Voting ||
                            session?.status === SessionStatus.Confirmed) && (
                            <SessionShareButton
                              canShare={canEditSession}
                              sessionId={sessionId ?? ''}
                              eventId={eventId ?? ''}
                              sessionStatus={session?.status}
                              userId={undefined}
                            />
                          )}
                        </>
                      )}
                  </View>
                )}
              </View>
              <ExpandableText
                text={session?.topic_description}
                charLimit={350}
              />
              {isManagerView &&
                session?.status === SessionStatus.Scheduled &&
                speakerModeratorEmails.length > 0 && (
                  <View style={styles.emailsContainer}>
                    {speakerModeratorEmails.map((email) => (
                      <TextReg key={email}>{email}</TextReg>
                    ))}
                  </View>
                )}
            </View>
            {speakers?.map((speaker) => {
              const handleProfilePress = () => {
                if (speaker.user_id) {
                  if (isManagerView) {
                    // In manager app, open community app in new tab
                    const url = `${COMMUNITY_APP_URL}/to/${speaker.user_id}`;
                    if (Platform.OS === 'web') {
                      window.open(url, '_blank');
                    } else {
                      void Linking.openURL(url);
                    }
                  } else {
                    // Regular navigation within the same app
                    router.navigate(`/to/${speaker.user_id}`);
                  }
                }
              };

              return (
                <TouchableOpacity
                  key={speaker.user_id}
                  style={styles.speakerInfo}
                  onPress={handleProfilePress}
                >
                  <ProfileResult userId={speaker.user_id} size="small" />
                  <ProfileBio userId={speaker.user_id} />
                </TouchableOpacity>
              );
            })}
          </ScrollView>
        )}

        {/* Getting There button (show for scheduled/confirmed sessions with location) */}
        {!isManagerView &&
          !isLoadingSession &&
          session?.location_id &&
          (session?.status === SessionStatus.Scheduled ||
            session?.status === SessionStatus.Confirmed) && (
            <View style={styles.gettingThereButtonContainer}>
              <TouchableOpacity
                style={styles.gettingThereButton}
                onPress={() => setIsGettingThereModalVisible(true)}
              >
                <TextBold style={styles.gettingThereButtonText}>
                  Getting There
                </TextBold>
              </TouchableOpacity>
            </View>
          )}

        {/* Session status menu button (manager view only) */}
        {isManagerView && !isLoadingSession && session && (
          <View
            style={
              isMobile
                ? styles.approvalButtonsContainerMobile
                : styles.approvalButtonsContainerDesktop
            }
          >
            <SessionStatusMenuButton
              sessionId={sessionId ?? ''}
              eventId={eventId ?? ''}
              currentStatus={session?.status}
              speakerUserIds={
                speakers?.map((speaker) => speaker.user_id ?? '') ?? []
              }
              communityId={communityId}
              existingStartDate={session?.start_date}
              existingEndDate={session?.end_date}
            />
          </View>
        )}
      </View>

      {/* Getting There Modal */}
      <GettingThereModal
        locationId={session?.location_id}
        visible={isGettingThereModalVisible}
        onClose={() => setIsGettingThereModalVisible(false)}
      />
    </Modal>
  );
}

const createStyles = ({ isMobile }: WithResponsive) =>
  StyleSheet.create({
    container: {
      flex: 1,
      width: '100%',
      justifyContent: 'flex-end',
    },
    scrollContent: {
      gap: 24,
      marginHorizontal: isMobile ? 16 : 32,
    },
    sessionInfo: {
      gap: 12,
    },
    timeContainer: {
      alignItems: 'center',
      justifyContent: 'center',
    },
    sessionTime: {
      fontSize: isMobile ? 14 : 16,
      color: Colors.text,
      opacity: 0.8,
      textAlign: 'center',
    },
    modalContent: {
      padding: 0,
      paddingTop: isMobile ? 8 : 24,
      paddingBottom: isMobile ? 24 : 32,
    },
    tagRow: {
      flexDirection: 'row',
      flexWrap: 'nowrap',
      justifyContent: 'flex-start',
      alignItems: 'center',
      gap: 8,
    },
    tagsContainer: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 8,
      flex: 1,
      minWidth: 0, // allow shrinking on web so action buttons have space
    },
    actionButtons: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      marginLeft: 'auto',
      flexShrink: 0,
    },
    titleRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    title: {
      fontSize: 20,
      flex: 1,
    },
    description: {
      fontSize: isMobile ? 14 : 16,
      paddingRight: isMobile ? 24 : 16,
    },
    speakerInfo: {
      gap: 12,
    },
    emailsContainer: {
      gap: 4,
    },

    loader: {
      position: 'absolute',
      top: 0,
      left: 0,
      bottom: 0,
      right: 0,
      backgroundColor: Colors.purple0,
    },
    approvalButtonsContainerDesktop: {
      position: 'absolute',
      bottom: 24,
      right: 32,
      zIndex: 10,
    },
    approvalButtonsContainerMobile: {
      width: '100%',
      alignItems: 'center',
      justifyContent: 'center',
      paddingTop: 16,
      marginTop: 16,
      borderTopWidth: 1,
      borderTopColor: Colors.purple1,
    },
    gettingThereButtonContainer: {
      width: '100%',
      alignItems: 'center',
      justifyContent: 'center',
      paddingTop: 16,
      marginTop: 16,
      borderTopWidth: 1,
      borderTopColor: Colors.purple1,
    },
    gettingThereButton: {
      backgroundColor: Colors.pink0,
      paddingHorizontal: 24,
      paddingVertical: 12,
      borderRadius: 8,
      alignItems: 'center',
      justifyContent: 'center',
    },
    gettingThereButtonText: {
      fontSize: 16,
      color: Colors.tint,
    },
  });
